import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import { OrganizationKind } from '../../graphql/__generated__/schema';
import PortalAccessDialog, { PortalAccessDirection } from './PortalAccessDialog';

interface Props {
    accountId: string;
    accountName: string;
    /** Null when the query has not answered yet, or answered without the field. */
    portalEnabled: boolean | null | undefined;
    /** Freelance.com is `internal`; every client account is not. */
    kind: OrganizationKind | null | undefined;
}

/**
 * The state of one account's portal access, and the gesture that changes it.
 *
 * **It lives on the account sheet since PCG-141**, first of the three panels, because
 * the three read as one sequence of decisions: may the account receive portal users,
 * what may it see, and what may it be shown inside one of those modules.
 *
 * Until that screen existed the panel hung off the **users list filtered on one
 * account** (PCG-79), filtering being what made an account the subject of a screen. The
 * pairing had a real merit worth naming, because the move costs it: the memberships
 * that make `ACCOUNT_HAS_PORTAL_USERS` refuse were listed right below, so the refusal
 * read as a description of what was on screen. Here it does not, which is why its
 * wording has to carry the fact by itself rather than point at a list.
 *
 * The user sheet was never a candidate: it describes a *person*, and a closed account
 * cannot appear on one (the invitation refuses on a closed account, and the closing
 * refuses while a membership lives), so the gesture would have had nothing to act on.
 *
 * The button is **never disabled on a rule the server owns**. Greying « Fermer »
 * because users remain would reimplement `ACCOUNT_HAS_PORTAL_USERS` in the front, where
 * it would drift, and would leave the operator with a dead control and no sentence. The
 * refusal is asked for, and rendered.
 */
const PortalAccessPanel = ({ accountId, accountName, portalEnabled, kind }: Props) => {
    const [direction, setDirection] = useState<PortalAccessDirection | null>(null);

    /**
     * Freelance.com carries no portal access at all: without this the panel showed
     * « Fermé au portail » on the internal organisation and offered to open it, a button
     * whose only outcome is `INTERNAL_ORGANIZATION_NOT_ALLOWED`. The onboarding tree
     * drops it for the same reason.
     *
     * Kept although the account sheet now says it in words before rendering any panel:
     * the guard is on the **nature of the organisation**, which is a property of the row
     * this component is given, and a component that refuses to state a fact it holds is
     * not made safer by a caller that happens to check first.
     *
     * This is **not** the same as leaving `ACCOUNT_HAS_PORTAL_USERS` to the server: that
     * one is a count that changes under us, this one is already in hand.
     */
    if (kind === OrganizationKind.Internal) {
        return null;
    }

    // Null is not « fermé ». A missing field would otherwise offer « Ouvrir » on an
    // account that may well be open already.
    if (portalEnabled === null || portalEnabled === undefined) {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border-norm bg-background-norm px-4 py-3">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-text-900">{accountName}</h2>
                    <Badge category={portalEnabled ? 'success' : 'default'} size="small">
                        {portalEnabled
                            ? c('Portal access').t`Open to the portal`
                            : c('Portal access').t`Closed to the portal`}
                    </Badge>
                </div>
                {/* « Ouvert » is an eligibility, and it is said here too rather than only
                    in the confirmation: this line is what a reader scanning the screen
                    takes away, and « ouvert » alone reads as « des gens y ont accès ». */}
                <p className="mt-1 text-xs text-text-600">
                    {portalEnabled
                        ? c('Portal access')
                              .t`This account can receive portal users. Each access is then granted by an invitation.`
                        : c('Portal access').t`No user can be invited until this account is opened.`}
                </p>
            </div>

            <Button className="ml-auto" category="weak" onClick={() => setDirection(portalEnabled ? 'close' : 'open')}>
                {portalEnabled
                    ? c('Portal access action').t`Close this account to the portal…`
                    : c('Portal access action').t`Open this account to the portal…`}
            </Button>

            {direction && (
                <PortalAccessDialog
                    accountId={accountId}
                    accountName={accountName}
                    direction={direction}
                    onClose={() => setDirection(null)}
                />
            )}
        </div>
    );
};

export default PortalAccessPanel;
